<template>
  <app-modal
    v-if="assessment"
    @close="$emit('close')"
  >
    <div
      v-if="isConfirmed"
      class="mb-4 text-center"
    >
      <app-badge
        :type="approved ? 'positive' : 'negative'"
        :icon="approved ? 'check' : 'important'"
        class=""
      >
        {{ approved ? 'Approved' : 'Rejected' }}
      </app-badge>
    </div>
    <app-card class="w-screen max-w-xl max mx-auto overflow-hidden is-borderless">
      <article>
        <header class="flex flex-wrap leading-tight bg-grey-100">
          <div class="p-6 flex-1 border-b-2 overflow-hidden">
            <p class="text-xs text-grey-700 mb-0">
              {{ assessment.company.name }} &ndash; {{ assessment.ref }}
            </p>
            <h1 class="text-2xl mb-0 truncate">
              {{ assessment.title }}
            </h1>
          </div>
          <div
            class="text-center flex items-center justify-center leading-none px-6 py-2 text-white flex-no-shrink"
            :class="gradingBgColorScore(totalPercentage)"
          >
            <div>
              <strong
                class="text-4xl block"
              >
                {{ totalPercentage }}<small>%</small>
              </strong>
              <template v-if="isContinuationPhase">
                {{ totalPercentageDifference }}
              </template>
              {{ totalScore }} / {{ maxScore }}
            </div>
          </div>
          <div
            v-if="booster"
            class="text-center flex items-center justify-center leading-none px-6 py-2 text-white flex-no-shrink"
            :class="gradingBgColorScore(totalBoosterPercentage)"
          >
            <div>
              <strong
                class="text-4xl block"
              >
                {{ totalBoosterPercentage }}<small>%</small>
              </strong>
              Booster {{ totalBoosterScore }} / {{ maxBoosterScore }}
            </div>
          </div>
        </header>
        <nav class="w-full border-b-4">
          <ul class="list-reset flex items-end -mb-1 mx-2">
            <li
              class="py-2 cursor-pointer border-b-4 px-4"
              :class="{
                'text-grey-700 border-transparent hover:border-primary-500 hover:text-primary-500':
                  active !== null,
                'text-primary-500 border-primary-500': active === null
              }"
              @click="active = null"
            >
              Summary
            </li>
            <li
              v-for="({id, name}) in assessors"
              :key="id"
              class="py-2 cursor-pointer border-b-4 px-4"
              :class="{
                'text-grey-700 border-transparent hover:border-primary-500 hover:text-primary-500':
                  active !== id,
                'text-primary-500 border-primary-500': active === id
              }"
              @click="active = id"
            >
              {{ name }}
            </li>
          </ul>
        </nav>
        <div v-show="!active">
          <template v-if="assessmentTemplate">
            <table class="w-full border-grey-300">
              <thead class="border-b-2">
                <tr>
                  <th class="stripes-grey"></th>
                  <th
                    v-for="({id, name}) in assessors"
                    :key="id"
                    class="px-4 py-2 border-l"
                  >
                    {{ name.split(" ")[0] }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-if="showRecommended"
                  class="border-b-4 border-t-4"
                >
                  <td class="px-4 py-2 font-bold">
                    Recommended
                  </td>
                  <td
                    v-for="({id}) in assessors"
                    :key="id"
                    class="text-center border-l"
                  >
                    <span
                      v-if="assessorRecommended(id) === null"
                      class="w-6 inline-flex items-center justify-center h-6 font-bold text-grey-500"
                    >
                      -
                    </span>
                    <span
                      v-else-if="!!assessorRecommended(id)"
                      class="w-6 inline-flex items-center justify-center h-6 font-bold text-white bg-positive-500"
                    >
                      &check;
                    </span>
                    <span
                      v-else
                      class="w-6 inline-flex items-center justify-center h-6 font-bold text-white bg-negative-500"
                    >
                      &times;
                    </span>
                  </td>
                </tr>
                <template v-for="(area, index) in assessmentTemplate">
                  <tr :key="index">
                    <th
                      v-if="assessmentTemplate.length > 1 && area.questions.filter(q => q.scorable || isBoosterQuestion(q.key) || q.type === 'boolean').length > 0"
                      :colspan="assessors.length + 1"
                      class="text-left px-4 py-2 border-b bg-grey-50"
                    >
                      {{ area.title }}
                    </th>
                  </tr>
                  <tr
                    v-for="question in area.questions.filter(q => q.scorable || isBoosterQuestion(q.key) || ['boolean'].includes(q.type))"
                    :key="question.key"
                    class="border-b"
                  >
                    <th
                      class="px-4 py-2 text-left font-normal"
                      v-html="parseMarkdown(question.title)"
                    />
                    <td
                      v-for="({id}) in assessors"
                      :key="id"
                      class="px-4 py-2 text-center border-l"
                    >
                      <template v-if="!question.scorable && !isBoosterQuestion(question.key) && question.type == 'boolean'">
                        <span
                          v-if="assessorMark(id, question.key) === 1"
                          class="w-8 inline-flex items-center justify-center h-8 bg-positive-500 text-white"
                        >
                          &check;
                        </span>
                        <span
                          v-else-if="assessorMark(id, question.key) === 0"
                          class="w-8 inline-flex items-center justify-center h-8 bg-negative-500 text-white"
                        >
                          &times;
                        </span>
                        <span
                          v-else-if="assessorMark(id, question.key) === 3"
                          class="w-8 inline-flex items-center justify-center h-8 bg-neutral-500 text-white"
                        >
                          &excl;
                        </span>
                        <span
                          v-else
                          class="w-8 inline-flex items-center justify-center h-8 bg-grey-300 text-grey-500 font-bold"
                        >
                          &mdash;
                        </span>
                      </template>
                      <template v-else>
                        {{ assessorMark(id, question.key) }}
                      </template>
                    </td>
                  </tr>
                </template>
              </tbody>
              <tfoot class="border-t-2 border-b-2 font-bold">
                <tr>
                  <td class="px-4 py-2 stripes-grey"></td>
                  <td
                    v-for="({id}) in assessors"
                    :key="id"
                    class="border-l px-4 py-2 text-center"
                  >
                    {{ assessorScore(id) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </template>
          <template v-else>
            <div class="m-8 border-4 border-dashed flex items-center justify-center">
              <h1>No assessment template has been set for this marking phase.</h1>
            </div>
          </template>
        </div>
        <div
          v-for="assessor in assessors"
          v-show="active === assessor.id"
          :key="assessor.id"
        >
          <template v-for="(area, index) in assessmentTemplate">
            <div :key="index">
              <h2 class="border-b-4 px-6 pb-2 pt-6 text-2xl">
                {{ area.title }}
              </h2>
              <template v-for="(question,qi) in area.questions">
                <div
                  :key="question.key"
                  class="px-6 py-4"
                  :class="{
                    'border-b' : qi + 1 !== area.questions.length
                  }"
                >
                  <template v-if="question.type === 'boolean'">
                    <div class="flex">
                      <div
                        class="flex-1"
                        v-html="parseMarkdown(question.title)"
                      />
                      <div class="ml-6 flex-no-shrink text-xl">
                        <div
                          v-if="getAssessorResponse(question.key) === 0"
                          class="flex items-center justify-center w-12 h-12 bg-negative-500 text-white"
                        >
                          &times;
                        </div>
                        <div
                          v-else-if="getAssessorResponse(question.key) === 1"
                          class="flex items-center justify-center w-12 h-12 bg-positive-500 text-white"
                        >
                          &check;
                        </div>
                        <div
                          v-else-if="getAssessorResponse(question.key) === 3"
                          class="flex items-center justify-center w-12 h-12 bg-neutral-500 text-white"
                        >
                          &excl;
                        </div>
                        <div
                          v-else
                          class="flex items-center justify-center w-12 h-12 bg-grey-300 text-grey-500 font-bold"
                        >
                          &mdash;
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="['option','select'].includes(question.type)">
                    <div class="flex items-start">
                      <div class="flex-1">
                        <div
                          class="mb-2"
                          v-html="parseMarkdown(question.title)"
                        />
                        <div
                          class="italic flex-1"
                          v-html="parseMarkdown(getQuestionOptionValue(question.key, getAssessorResponse(question.key)))"
                        />
                      </div>
                      <div
                        v-if="(question.scorable || isBoosterQuestion(question.key)) && getAssessorResponse(question.key)"
                        class="ml-12 w-12 h-12 flex items-center justify-center text-white text-xl font-bold"
                        :class="gradingBgColorScore((getAssessorResponse(question.key) / maxScoreForQuestion(question.options)) * 100)"
                      >
                        {{ getAssessorResponse(question.key) }}
                      </div>
                      <div
                        v-else-if="question.scorable"
                        class="flex items-center justify-center ml-12 w-12 h-12 bg-grey-300 text-grey-500 font-bold"
                      >
                        &mdash;
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div
                      class="font-bold mb-2"
                      v-html="parseMarkdown(question.title)"
                    />
                    <div
                      class="italic"
                      v-html="getAssessorResponse(question.key)"
                    />
                  </template>
                </div>
              </template>
            </div>
          </template>
        </div>
        <footer class="border-t px-6 py-2 flex">
          <div class="w-1/3">
            <icon-link
              v-if="previousAssessment"
              icon="arrow-thin-left-circle"
              href="#"
              @click.prevent="$emit('entry:change', previousAssessment.uuid)"
            >
              {{ previousAssessment.ref }}
            </icon-link>
          </div>
          <div class="w-1/3 text-center">
            <icon-link
              href="#"
              type="negative"
              @click.prevent="$emit('close')"
            >
              Close
            </icon-link>
          </div>
          <div class="w-1/3 text-right">
            <icon-link
              v-if="nextAssessment"
              icon="arrow-thin-right-circle"
              href="#"
              icon-placement="right"
              @click.prevent="$emit('entry:change', nextAssessment.uuid)"
            >
              {{ nextAssessment.ref }}
            </icon-link>
          </div>
        </footer>
      </article>
    </app-card>
    <div class="text-center text-white text-xs p-6 max-w-sm mx-auto">
      {{ assessmentDataIndex + 1 }} / {{ assessments.length }}
    </div>
  </app-modal>
</template>

<script>
import sum from 'lodash/sum'
export default {
  name: "LeagueTableEntryModal",
  props: {
    assessments: {
      type: Array,
      required: true
    },
    assessment: {
      type: Object,
      default: null,
      required: false
    },
    showRecommended: {
      type: Boolean,
      default: false
    },
    booster: {
      type: [Object, null],
      default: null
    }
  },
  data: () => ({ active: null }),
  computed: {
    assessmentTemplate() {
      return this.$page.marking_phase?.template?.assessment_areas ?? false
    },
    assessmentQuestions() {
      return this.assessmentTemplate?.map(area => area.questions).flat() ?? []
    },
    approved() {
      return this.assessment.pivot.approved_at
    },
    rejected() {
      return this.assessment.pivot.rejected_at
    },
    isConfirmed() {
      return this.assessment.pivot.confirmed_at
    },
    assessmentDataIndex() {
      return this.assessments.findIndex(
        assessment => assessment.uuid === this.assessment.uuid
      );
    },
    previousAssessment() {
      return this.assessments[this.assessmentDataIndex - 1];
    },
    nextAssessment() {
      return this.assessments[this.assessmentDataIndex + 1];
    },

    application() {
      return this.assessment.application || [];
    },

    assessors() {
      return this.assessment.assessors;
    },

    isContinuationPhase() {
      return !!this.$page.marking_phase.continuation_of_marking_phase_id
    },

    numberOfAssessors() {
      return this.assessment.assessors.length;
    },

    fullyAssessedCount() {
      return this.assessment.all_assessor_grades_current_phase.length
    },

    fullyAssessed() {
      return this.numberOfAssessors === this.fullyAssessedCount
    },

    // TOTALS
    totalScore() {
      return sum(this.allAssessorTotals)
    },

    totalBoosterScore() {
      return sum(this.allAssessorBoosterScores);
    },

    previousTotalScore() {
      return sum(this.allPreviousAssessorTotals)
    },

    // PERCENTAGE TOTAL

    totalBoosterPercentage() {
      if(!this.maxBoosterScore) return 0;
      return parseFloat(((this.totalBoosterScore / this.maxBoosterScore) * 100).toFixed(2));
    },

    totalPercentage() {
      const score = parseFloat(((this.totalScore / this.maxScore) * 100).toFixed(2))
      return isNaN(score) ? 0 : score;
    },

    previousTotalPercentage() {
      const score = parseFloat(((this.previousTotalScore / this.previousMaxScore) * 100).toFixed(2))
      return isNaN(score) ? 0 : score;
    },

    totalPercentageDifference() {
      const diff = (this.totalPercentage - this.previousTotalPercentage).toFixed(2);
      return `(${diff > 0 ? '+' + diff : diff}%)`
    },

    avaiableMarks() {
      return this.$page.marking_phase.template?.max_score ?? 0
    },

    maxScore() {
      return this.avaiableMarks * this.numberOfAssessors ?? 0
    },

    maxBoosterScore() {
      if (!this.booster) return 0;
      return (
        this.booster?.max_score * this.numberOfAssessors ?? 0
      );
    },

    previousMaxScore() {
      if(!this.assessment.all_assessor_grades_previous_phase) return 0
      return this.$page.marking_phase.template?.max_score * this.assessment.all_assessor_grades_previous_phase.length ?? 0
    },

    // AVERAGES
    validateBoosterQuestionScores() {
      if (!this.booster) return false;
      return this.assessment.all_assessor_grades_current_phase.some(assessorGrade => {
        let scores = assessorGrade.scores.filter(score => this.booster.questions.includes(score.key)).map(score => score.grade ?? 0);
        return sum(scores) > 0;
      });
    },

    allAssessorBoosterScores() {
      if (!this.validateBoosterQuestionScores) return [];
      return this.assessment.all_assessor_grades_current_phase.reduce(
        (arr, assessorGrade) => {
          let scores = assessorGrade.scores.filter(score => this.booster.scores.includes(score.key)).map(score => score.grade ?? 0);
          arr.push(...scores);
          return arr;
        },
        []
      );
    },

    allAssessorAverages() {
      return this.assessment.all_assessor_grades_current_phase.reduce((arr, assessorGrade) => {
        arr.push(assessorGrade.average);
        return arr;
      },[])
    },

    allPreviousAssessorAverages() {
      return this.assessment.all_assessor_grades_previous_phase.reduce((arr, assessorGrade) => {
        arr.push(assessorGrade.average);
        return arr;
      },[])
    },


    // TOTALS
    allAssessorTotals() {
      return this.assessment.all_assessor_grades_current_phase.reduce((arr, assessorGrade) => {
        arr.push(assessorGrade.total);
        return arr;
      },[])
    },

    allPreviousAssessorTotals() {
      if(!this.isContinuationPhase) return 0;
      return this.assessment.all_assessor_grades_previous_phase.reduce((arr, assessorGrade) => {
        arr.push(assessorGrade.total);
        return arr;
      },[])
    },

    // TOTAL RANGES
    minRangeScore() {
      const min = this.min(this.allAssessorTotals)
      return isNaN(min) ? '-' : min;
    },
    maxRangeScore() {
      const max = this.max(this.allAssessorTotals)
      return isNaN(max) ? '-' : max;
    },

    rangeScore() {
      const range = this.maxRangeScore - this.minRangeScore;
      return isNaN(range) ? '-' : range;
    },
  },
  methods: {
    maxScoreForQuestion(options) {
      return Math.max(...options.map(o => o?.value ?? o));
    },
    isBoosterQuestion(key) {
      return this.booster ? this.booster.scores.includes(key) : false;
    },
    findAssessor(assessorId) {
      return this.assessment.all_assessor_grades_current_phase.find(aag => aag.assessor.id === assessorId) ?? false
    },
    assessorScore(assessorId) {
      return this.findAssessor(assessorId)?.total ?? '-'
    },
    assessorRecommended(assessorId) {
      return this.findAssessor(assessorId)?.recommended ?? null
    },
    assessorMark(assessorId, key) {
      return this.findAssessor(assessorId)?.scores?.find(s => s.key === key)?.grade ?? '-'
    },
    assessorPercentage(assessorId) {
      return this.findAssessor(assessorId)?.percentage ?? 0
    },
    assessorComments(assessorId) {
      return this.findAssessor(assessorId)?.comments ?? '_Not yet started_'
    },
    gradingTextColorScore(percentage) {
      return {
        0: 'text-grading-value-1',
        10: 'text-grading-value-2',
        20: 'text-grading-value-3',
        30: 'text-grading-value-4',
        40: 'text-grading-value-5',
        50: 'text-grading-value-6',
        60: 'text-grading-value-7',
        70: 'text-grading-value-8',
        80: 'text-grading-value-9',
        90: 'text-grading-value-10',
        100: 'text-grading-value-10',
      }[Math.floor(percentage / 10) * 10]
    },
    gradingBgColorScore(percentage) {
      return {
        0: 'bg-grading-value-1',
        10: 'bg-grading-value-2',
        20: 'bg-grading-value-3',
        30: 'bg-grading-value-4',
        40: 'bg-grading-value-5',
        50: 'bg-grading-value-6',
        60: 'bg-grading-value-7',
        70: 'bg-grading-value-8',
        80: 'bg-grading-value-9',
        90: 'bg-grading-value-10',
        100: 'bg-grading-value-10',
      }[Math.floor(percentage / 10) * 10]
    },
    getQuestionOptionValue(key, value) {
      return this.assessmentQuestions.find(question => question.key === key)?.options.find(option => option?.value === value)?.label ?? ""
    },
    getAssessorGrades(assessorId) {
      return this.assessment.all_assessor_grades_current_phase?.find(({assessor_id}) => assessor_id === assessorId) ?? false
    },
    getAssessorResponse(key) {
      const assessorGrades = this.getAssessorGrades(this.active);
      if(!assessorGrades) return null
      if(['comments', 'comments_additional', 'recommended'].includes(key)) {
        return assessorGrades?.[key] ?? null;
      } else {
        const entry = assessorGrades.scores.find(score => score.key === key)
        return entry?.grade ?? entry?.value ?? null
      }
    }
  }
};
</script>
